import React from 'react';
import './floatingEventInfo.module.scss';

import iconHuman from '../images/icon-human.png';
import iconCalendar from '../images/icon-calendar.png';
import iconPlace from '../images/icon-place.png';
import { EventStatusUtils } from '../utils/EventStatusUtils';
import { SimpleDate, SimpleTimeRange } from '../../../../../admin/components/date/TimeRange';
import { Button } from '../../../../../commonv3/components/atoms/buttons/Button';
import { GlobalStatesV3 } from '../../../../../commonv3/states/GlobalStatesV3';
import { observer } from 'mobx-react';
import moment from 'moment-timezone';
import { EventWaitlistState } from '../states/EventWaitlistState';
import { EventWaitlistToggleButton } from './EventWaitlistToggleButton';
import { EventWaitlistDescription } from '../../../molecules/event_waitlists/EventWaitlistDescription';
import { SpecialEventEvents } from '../../../../../commonv3/events/SpecialEventEvents';
import { useEventReservationState } from '../states/EventPaneState';

type ButtonProps = {
  eventId: number;
};

const EventButton = observer(({ eventId }: ButtonProps) => {
  const {
    setProgress,
    event: eventResource,
    alreadyReserved,
    qualifiedToReserve,
    qualifiedToWatchVideo,
  } = useEventReservationState(eventId);
  const tokenLoading = GlobalStatesV3.currentUser.loading;
  const event = eventResource.get();
  // Past events
  const endsAt = moment(event.ends_at);
  if (endsAt.isBefore(moment())) {
    // If archive is available, show video link for v3 members
    if (event.has_archive && event.archive_video && qualifiedToWatchVideo) {
      return (
        <Button
          id="event-archive-button"
          styleName="button"
          type="primary"
          size="l"
          target="_blank"
          href={event.archive_video.url}
          onClick={() => SpecialEventEvents.reportViewArchive(event)}
        >
          動画を見る
        </Button>
      );
    } else {
      return (
        <Button
          id="past-event-button"
          styleName="button"
          type="primary"
          size="l"
          disabled={true}
          loading={tokenLoading}
        >
          終了しました
        </Button>
      );
    }
  }
  // On-going events : show live streaming link for v3 members if available
  if (
    moment(event.ends_at).isSameOrAfter(moment()) &&
    moment(event.starts_at).add(-10, 'minutes').isSameOrBefore(moment())
  ) {
    if (event.has_archive && event.archive_video && qualifiedToWatchVideo) {
      return (
        <Button
          styleName="button"
          type="notify"
          size="l"
          target="_blank"
          id="event-view-live-button"
          href={event.archive_video.url}
          onClick={() => SpecialEventEvents.reportViewLive(event)}
        >
          ライブ配信を見る
        </Button>
      );
    }
  }
  // Other events
  return (
    <Button
      id="event-reservation-button"
      styleName="button"
      type="primary"
      size="l"
      loading={tokenLoading}
      disabled={!tokenLoading && (EventStatusUtils.shouldDisable(alreadyReserved, event) || !qualifiedToReserve)}
      onClick={() => setProgress('reserve')}
    >
      {EventStatusUtils.getButtonText(alreadyReserved, event)}
    </Button>
  );
});

type Props = {
  eventId: number;
  alreadyReserved: boolean;
  eventWaitlistState: EventWaitlistState;
};

export const FloatingEventInfo = observer(({ eventId, alreadyReserved, eventWaitlistState }: Props) => {
  const { event: eventResource, qualifiedToReserve } = useEventReservationState(eventId);
  const event = eventResource.get();
  const { location } = event;
  return (
    <div id="floating-event-info" styleName="container floating-menu">
      <div styleName="row">
        <div styleName="left-column">
          <img src={iconCalendar} styleName="icon" />
        </div>
        <div styleName="right-column">
          <div styleName="hours">
            <SimpleDate date={event.starts_at} />
            <SimpleTimeRange range={event} />
          </div>
        </div>
      </div>
      <div styleName="row">
        <div styleName="left-column">
          <img src={iconPlace} styleName="icon" />
        </div>
        <div styleName="right-column">
          <div styleName="location">{location ? location.name : 'オンライン'}</div>
        </div>
      </div>
      {!EventStatusUtils.isEventFull(event) && (
        <React.Fragment>
          <div styleName="row">
            <div styleName="left-column">
              <img src={iconHuman} styleName="icon" />
            </div>
            <div styleName="right-column">
              <div styleName="reservation-count">
                申込者
                {event.active_reservation_count} / {event.capacity}名
              </div>
            </div>
          </div>
        </React.Fragment>
      )}
      <div styleName="button-wrapper">
        <EventButton eventId={event.id} />
        {qualifiedToReserve && (
          <EventWaitlistToggleButton event={event} reserved={alreadyReserved} eventWaitlistState={eventWaitlistState} />
        )}
      </div>
      {qualifiedToReserve && eventWaitlistState.canShowWaitlist(event, alreadyReserved) && <EventWaitlistDescription />}
    </div>
  );
});
